import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {ScrollToPlugin} from "gsap/ScrollToPlugin";
import {SplitText} from "gsap/SplitText";
import {CustomEase} from "gsap/CustomEase";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, SplitText, CustomEase);

// ============================================================================
// RESPONSIVE CONFIGURATION
// ============================================================================

const RESPONSIVE_CONFIG = {
    breakpoints: {
        desktop: "(min-width: 1025px)", mobile: "(max-width: 1024px)"
    },

    scroll: {
        desktop: {distance: 40000, scrub: 2, snapDelay: 0.2, snapDurationMax: 5},
        mobile: {distance: 40000, scrub: 1, snapDelay: 0.3, snapDurationMax: 3}
    },

    sections: {
        rocket: {
            desktop: {
                initialDuration: 15,
                scaleDuration: 20,
                scaleDelay: 5,
                textFadeDuration: 3,
                maxScale: 500,
                initialYPercent: 30,
                initialXPercent: -120
            }, mobile: {
                initialDuration: 10,
                scaleDuration: 15,
                scaleDelay: 3,
                textFadeDuration: 2,
                maxScale: 580,
                initialYPercent: 20,
                initialXPercent: -80
            }
        }, faces: {
            desktop: {scaleDuration: 20, textAnimDuration: 3, initialScale: 200},
            mobile: {scaleDuration: 15, textAnimDuration: 2, initialScale: 180}
        }, logo: {
            desktop: {revealDuration: 12, fadeOutDuration: 8, delayBeforeExit: 6},
            mobile: {revealDuration: 8, fadeOutDuration: 5, delayBeforeExit: 4}
        }, forWho: {
            desktop: {transitionDuration: 10, scaleDuration: 3, textDuration: 8, textStagger: 0.3},
            mobile: {transitionDuration: 6, scaleDuration: 2, textDuration: 5, textStagger: 0.2}
        }, about: {
            desktop: {charStagger: 0.1, fadeOutAmount: 6, fadeOutDelay: 5},
            mobile: {charStagger: 0.05, fadeOutAmount: 4, fadeOutDelay: 3}
        }, collapsible: {
            desktop: {
                slideDuration: 10, expandDuration: 7, staggerDelay: 6, imageXPercent: 250, textStagger: 1
            }, mobile: {
                slideDuration: 6, expandDuration: 3, staggerDelay: 6, imageXPercent: 250, textStagger: 0.5
            }
        }, gallery: {
            // Each entry: [duration, offset] — per gallery element
            desktop: [[40, -10],  // 1. Lucart Group
                [32, 12],   // 2. Lucart Professional
                [36, 10],   // 3. Plusline
                [29, 12],   // 4. Toscotec
                [32, 10],   // 5. Selene
                [23, 6],    // 6. Bridge Insurance Broker
                [32, 10],    // 7. Cassa di Risparmio di Volterra
                [26, 6],   // 8. Apicoltura Casentinese
                [30, 11],    // 9. Unicredit
                [22, 6],    // 10. Tissue Planet
                [24, 12],   // 11. Cartiere Modesto Cardella
                [27, -4],   // 12. iT's Tissue
                [26, 12],   // 13. Scuola Normale Superiore
                [27, 3],    // 14. Boero
                [32, 10],    // 15. Eil
                [28, 7],    // 16. Futura
            ], mobile: [[26, -10],    // 1. Lucart Group
                [30, 6],      // 2. Lucart Professional
                [28, 10.5],   // 3. Plusline
                [26, 8.5],      // 4. Toscotec
                [30, 6.5],      // 5. Selene
                [26.5, 11],   // 6. Bridge Insurance Broker
                [27, 6.5],      // 7. Cassa di Risparmio di Volterra
                [30, 7],      // 8. Apicoltura Casentinese
                [34, 9],      // 9. Unicredit
                [28, 12.5],      // 10. Tissue Planet
                [31, 8],      // 11. Cartiere Modesto Cardella
                [25, 10.5],      // 12. iT's Tissue
                [26, 7],      // 13. Scuola Normale Superiore
                [25, 8],      // 14. Boero
                [24, 6.5],      // 15. Eil
                [25, 7.5],      // 16. Futura
            ],
        }, logosGallery: {
            desktop: {fadeInDuration: 6, stagger: 2}, mobile: {fadeInDuration: 8, stagger: 2.8}
        }, ending: {
            desktop: {imgSlideInDuration: 12, fadeInDuration: 8, logoSlideDuration: 5, logoSlideDelay: 5},
            mobile: {imgSlideInDuration: 9, fadeInDuration: 5, logoSlideDuration: 3, logoSlideDelay: 3}
        }, footer: {
            desktop: {
                logoTranslationPercentage: -70,
                infoWrapperScreenPercentage: 30,
                rotationDuration: 10,
                slideDuration: 20,
                elementSlideDuration: 10,
                elementStagger: 0.5,
                finalFontSize: "10vh"
            }, mobile: {
                logoTranslationPercentage: -82,
                infoWrapperScreenPercentage: 15,
                rotationDuration: 10,
                slideDuration: 12,
                elementSlideDuration: 6,
                elementStagger: 0.3,
                finalFontSize: "14.5vw"
            }
        }
    }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getConfig(section, isMobile) {
    const breakpoint = isMobile ? 'mobile' : 'desktop';
    return RESPONSIVE_CONFIG.sections[section][breakpoint];
}

function getScrollConfig(isMobile) {
    return isMobile ? RESPONSIVE_CONFIG.scroll.mobile : RESPONSIVE_CONFIG.scroll.desktop;
}

// Cache CSS color variables once — these never change after initial render.
const cssVars = {};

function initCssVars() {
    const style = getComputedStyle(document.body);
    cssVars.primaryGray = style.getPropertyValue('--color-primary-gray').trim();
    cssVars.primaryContrast = style.getPropertyValue('--color-primary-contrast').trim();
    cssVars.purpleContrast = style.getPropertyValue('--color-purple-contrast').trim();
}

// ============================================================================
// SVG VIEWBOX ZOOM
// ============================================================================

const SVG_ZOOM_CONFIG = {
    rocket: {
        cx: 643.5, cy: 251, viewBox: {w: 1020.1, h: 566.6}, preserveAspectRatio: "xMidYMid meet"
    }, faces: {
        cx: 3209, cy: 2088, viewBox: {w: 6000, h: 4174}, preserveAspectRatio: "xMidYMid slice"
    }
};

function calcViewBox(config, zoomFactor, cx, cy) {
    const w = config.viewBox.w / zoomFactor;
    const h = config.viewBox.h / zoomFactor;
    const centerX = cx !== undefined ? cx : config.cx;
    const centerY = cy !== undefined ? cy : config.cy;
    return {x: centerX - w / 2, y: centerY - h / 2, w, h};
}

function applyViewBox(svg, vb) {
    svg.setAttribute("viewBox", `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
}

// ============================================================================
// SPLIT TEXT INITIALIZATION
// ============================================================================

function initializeSplitTexts() {
    return {
        rocket: SplitText.create(".rocket-text", {type: "words"}),
        perspective: SplitText.create("#faces__text_2", {type: "chars, words"}),
        formattedTruths: SplitText.create("#faces__text_1", {type: "words"}),
        forWhoText1: SplitText.create("#for_who__text_1", {type: "words"}),
        forWhoText2: SplitText.create("#for_who__text_2", {type: "words"}),
        forWhoText3: SplitText.create("#for_who__text_3", {type: "words"}),
        about: SplitText.create("#about__text", {type: "chars, words"}),
        whatText: SplitText.create("#what__text", {type: "lines"}),
        whatTitle: SplitText.create("#what__title", {type: "chars"}),
        howText: SplitText.create("#how__text", {type: "lines"}),
        howTitle: SplitText.create("#how__title", {type: "chars"}),
    };
}

// ============================================================================
// SECTION TIMELINES
// ============================================================================

function createRocketTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('rocket', isMobile);
    const zoomConfig = SVG_ZOOM_CONFIG.rocket;
    const rocketSvg = document.querySelector("#rocket-ship svg:not(#rocket-ship-png)");
    const rocketPng = document.querySelector("#rocket-ship-png");
    const rocketProxy = {zoom: 0.7};

    const initialVb = calcViewBox(zoomConfig, rocketProxy.zoom);
    applyViewBox(rocketSvg, initialVb);
    applyViewBox(rocketPng, initialVb);

    if (isMobile) {
        tl.fromTo("#rocket-ship", {
            yPercent: config.initialYPercent, xPercent: config.initialXPercent, scale: 1
        }, {
            yPercent: 0, xPercent: 0, scale: 4, duration: config.initialDuration, ease: "power3.out", force3D: true
        }, "<")
            .to(rocketProxy, {
                zoom: config.maxScale, duration: config.scaleDuration, ease: "power3.in", onUpdate: () => {
                    const vb = calcViewBox(zoomConfig, rocketProxy.zoom);
                    applyViewBox(rocketSvg, vb);
                    applyViewBox(rocketPng, vb);
                }
            }, `+=${config.scaleDelay}`)
            .to("html", {backgroundColor: cssVars.primaryContrast, duration: 5, ease: "power3.in"}, ">-5")
            .to(splitTexts.rocket.words, {
                yPercent: 100, autoAlpha: 0, stagger: 0.3, duration: config.textFadeDuration, ease: "none"
            });
    } else {
        tl.from("#rocket-ship", {
            yPercent: config.initialYPercent,
            xPercent: config.initialXPercent,
            duration: config.initialDuration,
            ease: "power3.out",
            force3D: true
        }, "<")
            .to(rocketProxy, {
                zoom: config.maxScale, duration: config.scaleDuration, ease: "power3.in", onUpdate: () => {
                    const vb = calcViewBox(zoomConfig, rocketProxy.zoom);
                    applyViewBox(rocketSvg, vb);
                    applyViewBox(rocketPng, vb);
                }
            }, `+=${config.scaleDelay}`)
            .to("html", {backgroundColor: cssVars.primaryContrast, duration: 5, ease: "power3.in"}, ">-5")
            .to(splitTexts.rocket.words, {
                yPercent: 100, autoAlpha: 0, stagger: 0.3, duration: config.textFadeDuration, ease: "none"
            });
    }

    return tl;
}

function createFacesTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('faces', isMobile);
    const zoomConfig = SVG_ZOOM_CONFIG.faces;
    const facesSvg = document.querySelector("#faces svg:not(#faces-png)");
    const facesPng = document.querySelector("#faces-png");
    const facesProxy = {zoom: config.initialScale};
    // Precompute center offset (zoom target → natural SVG center)
    const naturalCx = zoomConfig.viewBox.w / 2;
    const naturalCy = zoomConfig.viewBox.h / 2;
    const invInitial = 1 / config.initialScale;

    function facesCenterAtZoom(z) {
        // Blend factor based on 1/z: correction is proportional to viewBox size,
        // so visually imperceptible at high zoom, only applied as viewBox grows.
        const t = (1 / z - invInitial) / (1 - invInitial);
        return {
            cx: zoomConfig.cx + t * (naturalCx - zoomConfig.cx), cy: zoomConfig.cy + t * (naturalCy - zoomConfig.cy)
        };
    }

    const bgColor = cssVars.primaryGray;

    animateBgColor(tl, bgColor, 5, "none");
    tl.set(".faces-section", {autoAlpha: 1})
        .set("#faces", {autoAlpha: 1})
        .call(() => {
            const vb = calcViewBox(zoomConfig, facesProxy.zoom);
            applyViewBox(facesSvg, vb);
            applyViewBox(facesPng, vb);
        })
        .to("#rocket-ship", {autoAlpha: 0, duration: 1, ease: "none"})
        .set(".rocket-ship-section", {autoAlpha: 0})
        .from(splitTexts.formattedTruths.words, {yPercent: 100, autoAlpha: 0, duration: 3})
        .to(facesProxy, {
            zoom: 1, duration: config.scaleDuration, ease: "power4.out", onUpdate: () => {
                const {cx, cy} = facesCenterAtZoom(facesProxy.zoom);
                const vb = calcViewBox(zoomConfig, facesProxy.zoom, cx, cy);
                applyViewBox(facesSvg, vb);
                applyViewBox(facesPng, vb);
            }
        }, "<")
        .fromTo(splitTexts.perspective.chars, {z: 300, autoAlpha: 0}, {
            z: 0, autoAlpha: 1, stagger: {each: 0.2}, duration: config.textAnimDuration, ease: "none"
        }, ">")
        .to(splitTexts.perspective.words, {
            yPercent: -100, autoAlpha: 0, stagger: {each: 0.3, from: "end"}, duration: 1, ease: "none"
        }, "+=3")
        .to(splitTexts.formattedTruths.words, {
            yPercent: -100, autoAlpha: 0, stagger: {each: 0.3, from: "end"}, duration: 1, ease: "none"
        }, ">");

    return tl;
}

function createLindberghTimeline(isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('logo', isMobile);
    const bgColor = cssVars.primaryContrast;

    tl.set(".logo-section", {autoAlpha: 1})
        .to(".lindbergh-logo", {
            autoAlpha: 1, y: 0, duration: config.revealDuration, ease: "power2.inOut"
        }, "reveal")
        .to(".lindbergh-logo", {
            autoAlpha: 0, y: -100, duration: config.fadeOutDuration
        }, `+=${config.delayBeforeExit}`)
        .to("#faces", {
            autoAlpha: 0, duration: config.fadeOutDuration
        }, "<+2")
    animateBgColor(tl, bgColor, config.fadeOutDuration, "power1.inOut", "<");
    tl.set(".faces-section, .logo-section", {autoAlpha: 0});

    return tl;
}

// ============================================================================
// FOR-WHO SECTION
// ============================================================================

function createForWhoItemTimeline(imageId, textSplit, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('forWho', isMobile);

    tl.fromTo(`#${imageId}`, {yPercent: 100, scale: 0.9}, {
        yPercent: 0, duration: config.transitionDuration, ease: "power2.inOut", force3D: true
    })
        .to(`#${imageId}`, {scale: 1, duration: config.scaleDuration, ease: "power2.inOut", force3D: true}, ">")
        .fromTo(textSplit.words, {yPercent: -100, autoAlpha: 0}, {
            yPercent: 0,
            autoAlpha: 1,
            duration: config.textDuration,
            stagger: {each: config.textStagger, from: "start"},
            ease: "power2.inOut",
            force3D: true
        }, ">+1")
        .to(`#${imageId}`, {scale: 0.9, duration: config.scaleDuration, ease: "power2.inOut", force3D: true}, ">+3")
        .to(`#${imageId}`, {
            yPercent: -100, duration: config.transitionDuration, ease: "power2.inOut", force3D: true
        }, ">");

    return tl;
}

function createForWhoTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    tl.set(".for-who-section", {autoAlpha: 1});
    const items = [{imageId: "for_who__image_1", textSplit: splitTexts.forWhoText1}, {
        imageId: "for_who__image_2", textSplit: splitTexts.forWhoText2
    }, {imageId: "for_who__image_3", textSplit: splitTexts.forWhoText3}];

    items.forEach((item, index) => {
        tl.add(createForWhoItemTimeline(item.imageId, item.textSplit, isMobile), index === 0 ? "+=1" : ">");
    });

    // Slide out all texts
    ["#for_who__text_3", "#for_who__text_2", "#for_who__text_1"].forEach((selector, index) => {
        tl.to(selector, {
            xPercent: -100, autoAlpha: 0, stagger: {each: 0.3, from: "end"}, duration: 3, ease: "power2.in"
        }, index === 0 ? ">-10" : ">-2");
    });
    tl.set(".for-who-section", {autoAlpha: 0});

    return tl;
}

// ============================================================================
// ABOUT SECTION
// ============================================================================

function createAboutTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('about', isMobile);

    tl.set(".about-section", {autoAlpha: 1})
        .fromTo(splitTexts.about.chars, {autoAlpha: 0}, {
            autoAlpha: 1,
            stagger: config.charStagger,
            ease: "power2.inOut"
        })
        .to(splitTexts.about.chars, {
            autoAlpha: 0, y: -40, stagger: {amount: config.fadeOutAmount, from: "random"}
        }, `+=${config.fadeOutDelay}`)
        .set(".about-section", {autoAlpha: 0});

    return tl;
}

// ============================================================================
// COLLAPSIBLE SECTIONS (Unified)
// ============================================================================

function createCollapsiblesTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('collapsible', isMobile);

    tl.set(".collapsible-section", {autoAlpha: 1})
        .set("#what-canvas", {height: "100dvh"})
        .fromTo("#what-canvas", {xPercent: -100}, {xPercent: 0, duration: config.slideDuration, ease: "power2.inOut"})
        .fromTo(".collapsible-wrapper", {xPercent: 100}, {
            xPercent: 0, duration: config.slideDuration, ease: "power2.inOut", force3D: true
        }, "<")
    animateBgColor(tl, "black", config.slideDuration, "power2.inOut", "<");
    tl.fromTo(splitTexts.whatText.lines, {autoAlpha: 0}, {
        autoAlpha: 1, stagger: config.textStagger, duration: config.slideDuration, ease: "power2.inOut"
    }, ">+.5")
        .fromTo(splitTexts.whatTitle.chars, {autoAlpha: 0}, {
            autoAlpha: 1, stagger: {amount: 3, each: 0.1}, ease: "power2.inOut"
        }, ">+1")
        .fromTo("#what__image_start", {xPercent: -20, autoAlpha: 0}, {
            xPercent: 0, autoAlpha: 1, duration: 5, ease: "power2.inOut"
        }, ">-.5");
    if (isMobile) {
        const firstBgColor = document.querySelector('#what__collapsible_1').dataset.relatedColor;
        tl.to(splitTexts.whatText.lines, {
            autoAlpha: 0, yPercent: 20, stagger: config.textStagger, duration: 3, ease: "power2.inOut"
        })
            .to("#what-canvas", {
                height: "40dvh", duration: 4, ease: "power2.inOut"
            })
            .to(".collapsible-wrapper", {
                height: "60dvh", duration: 4, ease: "power2.inOut"
            }, "<");
        // Swap image and bg color during canvas resize
        animateBgColor(tl, firstBgColor, 4, "power2.inOut", "<", "#what-canvas");
        tl.to("#what__image_start", {xPercent: config.imageXPercent, duration: 4, ease: "power2.inOut"}, "<")
            .fromTo("#what__image_1", {xPercent: -config.imageXPercent}, {
                xPercent: 0, duration: 4, ease: "power2.inOut"
            }, "<");
    }
    tl.fromTo(".collapsible-wrapper .what-collapsible", {autoAlpha: 0, xPercent: 20}, {
        autoAlpha: 1, xPercent: 0, duration: 5, ease: "power2.inOut"
    }, "=-5");

    return tl;
}

function createCollapsibleSequence(splitTexts, isMobile, type, count) {
    const tl = gsap.timeline();
    const config = getConfig('collapsible', isMobile);
    const prefix = type; // 'what' or 'how'
    const textSplit = type === 'what' ? splitTexts.whatText : splitTexts.howText;

    // Track cumulative Y translation for mobile
    let cumulativeY = 24;

    // Helper: content element for collapsible N
    const contentEl = (n) => document.querySelector(`#${prefix}__collapsible_${n} .${prefix}-collapsible-content`);
    // Function-based height: scrollHeight works even on elements with height:0 inline,
    // and recalculates on invalidate (triggered by invalidateOnRefresh on resize).
    const autoHeight = (n) => () => contentEl(n).scrollHeight;

    // First collapsible expand (desktop only - mobile starts open)
    if (!isMobile) {
        tl.fromTo(contentEl(1), {
            height: 0,
        }, {
            height: autoHeight(1), duration: config.expandDuration, ease: "power2.inOut"
        }, 0.5);
    }

    // First image/bg swap — on mobile this already happened during canvas resize
    if (!isMobile) {
        const bgColor = document.querySelector(`#${prefix}__collapsible_1`).dataset.relatedColor;
        animateBgColor(tl, bgColor, 6, "power2.inOut", isMobile ? 0 : "<", `#${prefix}-canvas`);

        if (type === 'what') {
            tl.to("#what__image_start", {xPercent: config.imageXPercent, duration: 6, ease: "power2.inOut"}, "<")
                .fromTo("#what__image_1", {xPercent: -config.imageXPercent}, {
                    xPercent: 0, duration: 6, ease: "power2.inOut"
                }, "<");
        } else {
            tl.to("#how__image_start", {autoAlpha: 0, duration: 6, ease: "power2.inOut"}, "<")
                .fromTo("#how__image_1", {xPercent: config.imageXPercent}, {
                    xPercent: 0, duration: 6, ease: "power2.inOut"
                }, "<");
        }
    }
    tl.delay(config.staggerDelay)
    if (!isMobile) {
        tl.to(textSplit.lines, {
            autoAlpha: 0, yPercent: 20, stagger: config.textStagger, duration: 3, ease: "power2.inOut"
        }, "<");
    }

    // Subsequent collapsibles
    for (let i = 2; i <= count; i++) {
        const prevCollapsibleEl = document.querySelector(`#${prefix}__collapsible_${i - 1}`);

        if (isMobile) {
            // Add previous collapsible's height to cumulative translation
            cumulativeY += prevCollapsibleEl.scrollHeight + 3;
            tl.to(`.${prefix}-collapsible`, {
                y: -cumulativeY, duration: config.expandDuration, ease: "power2.inOut"
            }, `+=${config.staggerDelay}`);
        } else {
            tl.fromTo(contentEl(i), {
                height: 0,
            }, {
                height: autoHeight(i), duration: config.expandDuration, ease: "power2.inOut"
            }, `+=${config.staggerDelay}`)
                .fromTo(contentEl(i - 1), {height: autoHeight(i - 1)}, {
                    height: 0, duration: config.expandDuration, ease: "power2.inOut"
                }, "<");
        }

        const bgColor = document.querySelector(`#${prefix}__collapsible_${i}`).dataset.relatedColor;

        animateBgColor(tl, bgColor, 6, "power2.inOut", "<", `#${prefix}-canvas`);

        if (type === 'what') {
            tl.to(`#what__image_${i - 1}`, {xPercent: config.imageXPercent, duration: 6, ease: "power2.inOut"}, "<")
                .fromTo(`#what__image_${i}`, {xPercent: -config.imageXPercent}, {
                    xPercent: 0, duration: 6, ease: "power2.inOut"
                }, "<");
        } else {
            tl.to(`#how__image_${i - 1}`, {
                xPercent: -config.imageXPercent, autoAlpha: 0, duration: 6, ease: "power2.inOut"
            }, "<")
                .fromTo(`#how__image_${i}`, {xPercent: config.imageXPercent}, {
                    xPercent: 0, autoAlpha: 1, duration: 6, ease: "power2.inOut"
                }, "<");
        }
    }
    // Close final collapsible

    if (!isMobile) {
        tl.fromTo(contentEl(count), {height: autoHeight(count)}, {
            height: 0, duration: config.expandDuration, ease: "power2.inOut"
        }, `+=${config.staggerDelay}`);
    }

    return tl;
}

function createCollapsibleTransition(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('collapsible', isMobile);

    if (isMobile) {
        tl.to("#what-canvas >*", {
            autoAlpha: 0, duration: config.slideDuration, ease: "power2.inOut"
        }, "<")
            .to(".collapsible-wrapper .what-collapsible", {
                autoAlpha: 0, duration: config.slideDuration, ease: "power2.inOut"
            }, "<")
            .set(".collapsible-wrapper .what-collapsible", {display: "none"})
            .set("#how-canvas", {
                position: "absolute", top: 0, left: 0, width: "100%", height: "100dvh", xPercent: 100
            })
            .to(".collapsible-wrapper", {
                height: "0", duration: config.slideDuration, ease: "power2.inOut"
            }, "<")
            .to("#what-canvas", {
                height: "100dvh", duration: 3, ease: "power2.inOut"
            }, "<")
            .to("#what-canvas", {
                xPercent: -100, duration: config.slideDuration, ease: "power2.inOut"
            }, ">")
            .to("#how-canvas", {
                xPercent: 0, duration: config.slideDuration, ease: "power2.inOut"
            }, "<")
        animateBgColor(tl, cssVars.purpleContrast, config.slideDuration, "power2.inOut", "<");
        tl.fromTo(splitTexts.howText.lines, {
            autoAlpha: 0
        }, {
            autoAlpha: 1, stagger: config.textStagger, duration: config.slideDuration, ease: "power2.inOut"
        }, ">+.5")
            .fromTo(splitTexts.howTitle.chars, {
                autoAlpha: 0
            }, {
                autoAlpha: 1, stagger: {amount: 3, each: 0.1}, ease: "power2.inOut"
            }, "<+5")
            .fromTo("#how__image_start", {
                autoAlpha: 0
            }, {
                autoAlpha: 1, duration: 5, ease: "power2.inOut"
            }, "<")
            .to(splitTexts.howText.lines, {
                autoAlpha: 0, yPercent: 20, stagger: config.textStagger, duration: 3, ease: "power2.inOut"
            }, ">+15")
            .to(".collapsible-wrapper", {
                height: "60dvh", duration: config.slideDuration, ease: "power2.inOut"
            }, "<")
            .to("#how-canvas, #what-canvas", {
                height: "40dvh", duration: 3, ease: "power2.inOut"
            }, ">");
        // Swap image and bg color during canvas resize
        const firstHowBgColor = document.querySelector('#how__collapsible_1').dataset.relatedColor;
        animateBgColor(tl, firstHowBgColor, 3, "power2.inOut", "<", "#how-canvas");
        tl.to("#how__image_start", {autoAlpha: 0, duration: 3, ease: "power2.inOut"}, "<")
            .fromTo("#how__image_1", {xPercent: config.imageXPercent}, {
                xPercent: 0, duration: 3, ease: "power2.inOut"
            }, "<")
            .fromTo(".collapsible-wrapper .how-collapsible", {autoAlpha: 0, xPercent: -20}, {
                autoAlpha: 1, xPercent: 0, duration: 5, ease: "power2.inOut"
            }, "<");
    } else {
        tl.to("#what-canvas >*", {autoAlpha: 0, duration: config.slideDuration, ease: "power2.inOut"}, "<")
            .to(".collapsible-wrapper", {xPercent: -150, duration: config.slideDuration, ease: "power2.inOut"}, "<")
            .to(".collapsible-wrapper .what-collapsible", {
                autoAlpha: 0, duration: config.slideDuration, ease: "power2.inOut"
            }, "<")
            .to("#how-canvas", {xPercent: -100, duration: config.slideDuration, ease: "power2.inOut"}, "<")
            .set(".collapsible-wrapper .what-collapsible", {display: "none"})
            .fromTo(splitTexts.howText.lines, {autoAlpha: 0}, {
                autoAlpha: 1, stagger: config.textStagger, duration: config.slideDuration, ease: "power2.inOut"
            }, ">+.5")
            .fromTo(splitTexts.howTitle.chars, {autoAlpha: 0}, {
                autoAlpha: 1, stagger: {amount: 3, each: 0.1}, ease: "power2.inOut"
            }, "<+5")
            .fromTo("#how__image_start", {autoAlpha: 0}, {autoAlpha: 1, duration: 5, ease: "power2.inOut"}, "<")
            .fromTo(".collapsible-wrapper .how-collapsible", {autoAlpha: 0, xPercent: -20}, {
                autoAlpha: 1, xPercent: 0, duration: 5, ease: "power2.inOut"
            }, ">");
    }

    return tl;
}

function createCollapsibleFadeOut(isMobile) {
    const tl = gsap.timeline();
    const bgColor = cssVars.primaryContrast;
    tl.to('.collapsible-section', {autoAlpha: 0, duration: 5, ease: "power2.inOut"}, ">+5");
    animateBgColor(tl, bgColor, 5, "power2.inOut", "<");

    return tl;
}

// ============================================================================
// LOGOS GALLERY SECTION
// ============================================================================

function createLogosGalleryTimeline(isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('logosGallery', isMobile);

    if (isMobile) {
        tl.set(".logos-gallery-section", {pointerEvents: "auto", autoAlpha: 1})
            .to(".logos-gallery-section", {yPercent: -80, duration: 100, ease: "power3.inOut"})
            .to(".logos-gallery-section .logo-wrapper", {
                autoAlpha: 1, duration: config.fadeInDuration, stagger: {each: config.stagger}, ease: "power2.in"
            }, "<-2")
            .to(".logos-gallery-section .logo-wrapper", {
                autoAlpha: 0, duration: config.fadeInDuration, stagger: {each: config.stagger}, ease: "power2.in"
            }, "<+26")
            .set(".logos-gallery-section", {pointerEvents: "none", autoAlpha: 0});
    } else {

        tl.set(".logos-gallery-section", {pointerEvents: "auto", autoAlpha: 1})
            .to(".logos-gallery-section .logo-wrapper", {
                autoAlpha: 1, duration: config.fadeInDuration, stagger: {each: config.stagger}, ease: "power2.in"
            })
            .to(".logos-gallery-section .logo-wrapper", {
                autoAlpha: 0, duration: config.fadeInDuration, stagger: {each: config.stagger}, ease: "power2.in"
            }, ">-2")
            .set(".logos-gallery-section", {pointerEvents: "none", autoAlpha: 0});
    }

    return tl;
}

// ============================================================================
// GALLERY SECTION
// ============================================================================

function createGalleryTimeline(splitTexts, isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('gallery', isMobile);

    tl.set(".gallery-section", {autoAlpha: 1})

    // Cache all gallery elements before the loop to avoid repeated DOM queries
    const galleryElements = [];
    for (let i = 1; i <= 16; i++) {
        const element = document.getElementById(`gallery__element_${i}`);
        if (element) {
            galleryElements.push({element, index: i, targetY: (window.innerHeight + element.scrollHeight + 200) * -1});
        }
    }

    // Gallery elements animation with cached elements
    galleryElements.forEach(({element, index, targetY}) => {
        const [duration, offset] = config[index - 1];
        const position = index === 1 ? `=${offset}` : `<+${offset}`;

        tl.to(element, {
            y: `${targetY}px`, duration, ease: "none", force3D: true
        }, position);
    });
    tl.set(".gallery-section", {autoAlpha: 0});

    return tl;
}

// ============================================================================
// ENDING SECTION
// ============================================================================

function createEndingTimeline(isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('ending', isMobile);

    tl.set(".ending-section, .ending-text, .footer-upper-info-wrapper", {autoAlpha: 1});
    animateBgColor(tl, "black", 2, "power2.inOut");
    tl.fromTo(".ending-section img", {yPercent: 100}, {
        yPercent: 0, duration: config.imgSlideInDuration, ease: "power2.out"
    }, "<")
        .fromTo(".footer-upper-info-inner-wrapper", {yPercent: 100}, {
            yPercent: 0, duration: config.logoSlideDuration, ease: "power2.inOut"
        }, `>+${config.logoSlideDelay}`)
        .to(".ending-text h5,.ending-text h6", {
            autoAlpha: 1, duration: config.fadeInDuration, ease: "power2.out"
        }, ">6");

    return tl;
}

// ============================================================================
// FOOTER SECTION
// ============================================================================

function createFooterTimeline(isMobile) {
    const tl = gsap.timeline();
    const config = getConfig('footer', isMobile);

    tl.set(".footer-section", {autoAlpha: 1})
        .to(".footer-upper-info-wrapper", {
            yPercent: config.logoTranslationPercentage, duration: config.slideDuration, ease: "power2.inOut"
        }, ">+5")
        .to(".footer-upper-info-inner-wrapper", {
            yPercent: 130, duration: config.slideDuration, ease: "power2.inOut"
        }, "<")
        .to(".footer-logo", {fontSize: config.finalFontSize, duration: config.slideDuration, ease: "power2.inOut"}, "<")
        .to(".ending-text h5, .ending-text h6", {
            rotateX: 95, duration: config.rotationDuration, ease: "power2.out"
        }, "<")
        .fromTo(".footer-section", {yPercent: 100}, {
            yPercent: 0, duration: config.slideDuration, ease: "power2.inOut"
        }, "<")
        .fromTo(".footer-ending-text", {rotateX: -95}, {
            rotateX: 0, duration: config.rotationDuration, ease: "power2.out"
        },)
        .to(".footer-element", {
            autoAlpha: 1,
            y: 0,
            pointerEvents: "auto",
            duration: config.elementSlideDuration,
            stagger: {each: config.elementStagger, from: "start"},
            ease: "power2.out"
        });

    return tl;
}

// ============================================================================
// MAIN TIMELINE
// ============================================================================

const metaTheme = document.querySelector('meta[name="theme-color"]');

function setThemeColor(color) {
    if (metaTheme) metaTheme.setAttribute('content', color);
}

function syncThemeColor() {
    setThemeColor(getComputedStyle(document.documentElement).backgroundColor);
}

/**
 * Animate html backgroundColor (+ optional extra target) and sync
 * the meta theme-color tag on every frame this tween is active.
 */
function animateBgColor(tl, color, duration, ease, position, extraTarget) {
    const targets = extraTarget ? `${extraTarget}, html` : "html";
    tl.to(targets, {
        backgroundColor: color, duration, ease: ease || "none", onUpdate: syncThemeColor
    }, position);
}


function createMainTimeline(splitTexts, isMobile) {
    const scrollConfig = getScrollConfig(isMobile);

    const mainTl = gsap.timeline({
        scrollTrigger: {
            trigger: "main",
            start: "top top",
            end: `+=${scrollConfig.distance}`,
            scrub: scrollConfig.scrub,
            pin: true,
            normalizeScroll: true,
            invalidateOnRefresh: true,
            onUpdate: syncThemeColor
        }
    });

    mainTl
        .addLabel("start")
        .add(createRocketTimeline(splitTexts, isMobile))
        .add(createFacesTimeline(splitTexts, isMobile))
        .add(createLindberghTimeline(isMobile))
        .add(createForWhoTimeline(splitTexts, isMobile))
        .addLabel("who-section")
        .add(createAboutTimeline(splitTexts, isMobile))
        .add(createCollapsiblesTimeline(splitTexts, isMobile))
        .addLabel("what-section")
        .add(createCollapsibleSequence(splitTexts, isMobile, 'what', 4))
        .add(createCollapsibleTransition(splitTexts, isMobile))
        .addLabel("how-section")
        .add(createCollapsibleSequence(splitTexts, isMobile, 'how', 2))
        .add(createCollapsibleFadeOut(isMobile))
        .addLabel("gallery-section")
        .add(createLogosGalleryTimeline(isMobile))
        .add(createGalleryTimeline(splitTexts, isMobile))
        .addLabel("ending-section")
        .add(createEndingTimeline(isMobile), ">-6")
        .add(createFooterTimeline(isMobile))
        .addLabel("footer-section");

    return mainTl;
}

// ============================================================================
// INITIALIZATION
// ============================================================================

let mainTimeline = null;
let currentSplitTexts = null;

// Cleanup function for timeline and SplitText instances
function cleanupTimeline() {
    if (mainTimeline) {
        mainTimeline.kill();
        if (mainTimeline.scrollTrigger) {
            mainTimeline.scrollTrigger.kill();
        }
        mainTimeline = null;
    }
}

function cleanupSplitTexts() {
    if (currentSplitTexts) {
        Object.values(currentSplitTexts).forEach(split => {
            if (split && typeof split.revert === 'function') {
                split.revert();
            }
        });
        currentSplitTexts = null;
    }
}

function closeMenu(burger) {
    if (!burger.classList.contains("open")) return;
    let menuLinesTranslation = 20; // Should match the translation used in openMenu

    if (window.innerWidth <= 1024) {
        // For mobile, we need to reset the translation applied to the menu lines
        menuLinesTranslation = 10
    }

    burger.classList.remove("open");
    burger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = "auto";

    const ease = "power3.inOut";
    let tl = gsap.timeline({defaults: {duration: 0.3, ease}});
    tl.to("main", {xPercent: 0, duration: 1}, 0)
        .to(".menu-underlay", {xPercent: 120, duration: 1}, "<")
        .set(".pin-spacer", {pointerEvents: "auto"})
        .to(".menu-item", {xPercent: 30, autoAlpha: 0, stagger: .1, duration: .4, ease: "power3.out"}, 0)
        .to(".menu-top-line", {rotation: 0}, 0)
        .to(".menu-center-line", {rotation: 0}, 0)
        .to(".menu-bottom-line", {rotation: 0}, 0)
        .to(".menu-top-line", {y: -1 * menuLinesTranslation}, ">+.35")
        .to(".menu-bottom-line", {y: menuLinesTranslation}, "<");

    return tl;
}

function openMenu(burger) {
    if (burger.classList.contains("open")) return;

    burger.classList.add("open");
    burger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = "hidden";

    const ease = "power3.inOut";
    let tl = gsap.timeline({defaults: {duration: 0.3, ease}});
    tl.to("main", {xPercent: -120, duration: 1}, 0)
        .to(".menu-underlay", {xPercent: 0, duration: 1}, "<")
        .set(".pin-spacer", {pointerEvents: "none"})
        .to(".menu-top-line", {y: 0}, 0)
        .to(".menu-bottom-line", {y: 0}, 0)
        .fromTo(".menu-item", {xPercent: 30, autoAlpha: 0}, {
            xPercent: 0, autoAlpha: 1, stagger: .15, duration: .3, ease: "power3.out"
        }, 0.5)
        .to(".menu-top-line", {rotation: 45}, 0.35)
        .to(".menu-center-line", {rotation: 45}, "<")
        .to(".menu-bottom-line", {rotation: -45}, "<");

    return tl;
}

function scrollToLabel(label, offset = 0) {
    if (!mainTimeline || !mainTimeline.scrollTrigger) return;

    const scrollPos = mainTimeline.scrollTrigger.labelToScroll(label) + offset;
    let tl = gsap.timeline();

    tl.to("#menu_transition_overlay", {autoAlpha: 1, duration: 0.5, ease: "power2.inOut"})
        .to(window, {
            scrollTo: scrollPos, duration: .1, ease: "power2.inOut"
        })
        .to("#menu_transition_overlay", {autoAlpha: 0, duration: 0.5, ease: "power2.inOut"}, ">+.8");
}

document.addEventListener("DOMContentLoaded", () => {
    initCssVars();

    // Set current year in footer
    document.getElementById('now').textContent = new Date().getFullYear();

    // Preload PNGs so they're cached before user scrolls to their sections
    ['#rocket-ship-png image', '#faces-png image'].forEach(selector => {
        const href = document.querySelector(selector)?.getAttribute('href');
        if (href) {
            const img = new Image();
            img.src = href;
        }
    });

    // Scroll-hint: hide on scroll, show again after 4s idle
    const scrollHint = document.querySelector('.scroll-hint');
    const scrollHintSpans = scrollHint.querySelectorAll('span');
    let scrollIdleTimer = null;
    let scrollHintFadeTimer = null;

    function hideScrollHint() {
        clearTimeout(scrollIdleTimer);
        clearTimeout(scrollHintFadeTimer);
        scrollHint.classList.add('is-hidden');
        // Wait for opacity transition (0.5s) before killing animation
        scrollHintFadeTimer = setTimeout(() => {
            scrollHintSpans.forEach(s => s.style.animationName = 'none');
        }, 500);
    }

    function showScrollHint() {
        // Don't show if menu is open or timeline reached the end
        const menuOpen = document.querySelector('.menu-trigger')?.classList.contains('open');
        const atEnd = mainTimeline && mainTimeline.progress() >= 1;
        if (menuOpen || atEnd) return;

        clearTimeout(scrollHintFadeTimer);
        // Restore animation first, then remove hidden class so it fades in fresh
        scrollHintSpans.forEach(s => s.style.animationName = '');
        scrollHint.classList.remove('is-hidden');
    }

    window.addEventListener('scroll', () => {
        hideScrollHint();
        scrollIdleTimer = setTimeout(showScrollHint, 3000);
    }, {passive: true});

    // Prevent expensive full ScrollTrigger refresh on mobile address bar show/hide
    ScrollTrigger.config({ignoreMobileResize: true});

    // Use matchMedia for responsive timelines
    ScrollTrigger.matchMedia({
        // Desktop
        "(min-width: 1025px)": function () {
            // Cleanup previous instances on breakpoint change
            cleanupTimeline();
            cleanupSplitTexts();

            // Initialize fresh SplitText instances
            currentSplitTexts = initializeSplitTexts();

            mainTimeline = createMainTimeline(currentSplitTexts, false);

            // Return cleanup function for when leaving this breakpoint
            return function () {
                cleanupTimeline();
                cleanupSplitTexts();
            };
        },

        // Mobile
        "(max-width: 1024px)": function () {
            // Cleanup previous instances on breakpoint change
            cleanupTimeline();
            cleanupSplitTexts();

            // Initialize fresh SplitText instances
            currentSplitTexts = initializeSplitTexts();

            mainTimeline = createMainTimeline(currentSplitTexts, true);

            // Return cleanup function for when leaving this breakpoint
            return function () {
                cleanupTimeline();
                cleanupSplitTexts();
            };
        }
    });

    // Park underlay off-screen so GSAP owns the xPercent from the start
    gsap.set(".menu-underlay", {xPercent: 120});

    const burger = document.querySelector('.menu-trigger');

    // Burger toggle
    burger.addEventListener('click', () => {
        if (burger.classList.contains("open")) {
            closeMenu(burger);
        } else {
            openMenu(burger);
        }
    });

    // Menu item clicks - scroll to label
    document.querySelectorAll('[data-scroll-to]').forEach(item => {
        item.addEventListener('click', () => {
            const label = item.dataset.scrollTo;
            let offset = 0;
            const closeTl = closeMenu(burger);

            if (item.dataset.scrollDesktopOffset && window.innerWidth > 1024) {
                offset = parseFloat(item.dataset.scrollDesktopOffset);
            } else if (item.dataset.scrollMobileOffset && window.innerWidth <= 1024) {
                offset = parseFloat(item.dataset.scrollMobileOffset);
            }

            if (closeTl) {
                closeTl.eventCallback("onComplete", () => scrollToLabel(label, offset));
            } else {
                scrollToLabel(label, offset);
            }
        });
    });
});
